import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Entity {
  id: string;
}

export interface LoadingState {
  loading: boolean;
  error: string | null;
}

export interface EntitiesResult<T> {
  items: T[];
  total?: number;
  hasAdditionalActions?: boolean;
}

export interface EntityState<T> extends LoadingState {
  entities: { [key: string]: T };
  total: number;
}

export const baseInitialState = <T>() =>
  ({
    entities: {},
    loading: false,
    total: 0,
    error: null,
  }) as EntityState<T>;

function startLoading(state: LoadingState) {
  state.loading = true;
  state.error = null;
}

function finishLoading(state: LoadingState) {
  state.loading = false;
  state.error = null;
}

function setTotalCount<T>(state: EntityState<T>, { payload }: PayloadAction<number>) {
  state.total = payload;
}

function loadingFailed(state: LoadingState, action: PayloadAction<string>) {
  if (process.env.NODE_ENV === 'development') {
    console.error(action.type, action.payload);
  }

  const isCanceled = action.payload?.includes('CanceledError');

  state.loading = false;
  state.error = isCanceled ? null : action.payload;
}

const getEntitiesSuccess =
  <T>(getId: (entity: T) => string) =>
  (state: EntityState<T>, { payload: { items, total, hasAdditionalActions } }: PayloadAction<EntitiesResult<T>>) => {
    items.forEach((entity) => {
      const id = getId(entity);

      if (id) {
        state.entities[id] = entity;
      }
    });

    state.total = total != null ? total : Object.keys(state.entities).length;

    if (!hasAdditionalActions) {
      state.loading = false;
    }
  };

const getEntitiesMapSuccess = <T>(state: EntityState<T>, { payload }: PayloadAction<{ [id: string]: T }>) => {
  state.entities = {
    ...state.entities,
    ...payload,
  };
  state.loading = false;
};

const getEntitySuccess =
  <T>(getId: (entity: T) => string) =>
  (state: EntityState<T>, { payload }: PayloadAction<T>) => {
    const id = getId(payload);

    if (id) {
      state.entities[id] = {
        ...state.entities[id],
        ...payload,
      };
      state.total = state.total + 1;
      state.loading = false;
      state.error = null;
    }
    state.loading = false;
  };

export const loadingReducers = {
  startLoading,
  finishLoading,
  loadingFailed,
};

export const baseEntityReducers = <T>(getId: (entity: T | Entity) => string = (entity) => (entity as Entity).id) => ({
  ...loadingReducers,
  setTotalCount,
  getEntitiesSuccess: getEntitiesSuccess(getId),
  getEntitiesMapSuccess,
  getEntitySuccess: getEntitySuccess(getId),
});

const actionsTypeHelper = createSlice({
  name: 'actionsTypeHelper' as string,
  initialState: baseInitialState(),
  reducers: baseEntityReducers(),
}).actions;

export type BaseEntitiesActions = typeof actionsTypeHelper;
